import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Alert, Button, Snackbar, Stack } from '@mui/material';
import Close from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';

export type ToastSeverity = 'success' | 'error' | 'info' | 'warning';

/**
 * Atlas "With Multiple Actions" toast: text buttons Dismiss + one action. A toast with an
 * action stays until the user acts — Atlas says not to auto-dismiss errors.
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
  /** Label of the text Dismiss button shown next to the action. */
  dismissLabel?: string;
}

export interface ToastOptions {
  action?: ToastAction;
}

interface ToastMessage {
  id: string;
  message: string;
  severity: ToastSeverity;
  duration?: number | null;
  action?: ToastAction;
}

interface ToastContextValue {
  showToast: (
    message: string,
    severity?: ToastSeverity,
    duration?: number,
    options?: ToastOptions,
  ) => void;
  showSuccess: (message: string) => void;
  showError: (message: string, options?: ToastOptions) => void;
  showInfo: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = useCallback(
    (message: string, severity: ToastSeverity = 'info', duration = 5000, options?: ToastOptions) => {
      const id = Date.now().toString();
      const action = options?.action;
      setToast({ id, message, severity, duration: action ? null : duration, action });
    },
    [],
  );

  const showSuccess = useCallback(
    (message: string) => {
      showToast(message, 'success', 5000);
    },
    [showToast],
  );

  const showError = useCallback(
    (message: string, options?: ToastOptions) => {
      showToast(message, 'warning', 8000, options);
    },
    [showToast],
  );

  const showInfo = useCallback(
    (message: string) => {
      showToast(message, 'info', 5000);
    },
    [showToast],
  );

  const handleClose = () => {
    setToast(null);
  };

  const toastAction = toast?.action;
  const dismissAction = toastAction ? (
    <Stack direction="row" spacing={1}>
      <Button variant="text" size="medium" onClick={handleClose}>
        {toastAction.dismissLabel ?? 'Dismiss'}
      </Button>
      <Button
        variant="text"
        size="medium"
        onClick={() => {
          handleClose();
          toastAction.onClick();
        }}
      >
        {toastAction.label}
      </Button>
    </Stack>
  ) : (
    <Button variant="text" size="medium" onClick={handleClose}>
      <Close aria-label="Dismiss" />
    </Button>
  );

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showInfo }}>
      {children}
      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.duration}
        onClose={(_event, reason) => {
          // An action toast waits for Dismiss or the action, not a click elsewhere
          if (reason === 'clickaway' && toastAction) return;
          handleClose();
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={(theme) => ({
          zIndex: theme.zIndex.snackbar,
          top: 'var(--atlas-global-header-height, 64px)',
        })}
      >
        <Alert
          severity={toast?.severity || 'info'}
          action={dismissAction}
          aria-live="polite"
          sx={{ minWidth: 300 }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
